/**
 * A broadcast of "an HTTP rejection is about to reach its caller".
 *
 * Purely a transport concern: this file knows nothing about what any particular
 * rejection means. It exists so cross-cutting recovery can live in the domain that
 * understands the failure instead of being written into the shared axios instance —
 * the same separation the 401 path already uses, where the transport dispatches
 * `auth:unauthorized` rather than importing auth state.
 *
 * ## Why an event rather than an interceptor a consumer registers
 *
 * `webpack`'s `shared` scope contains `@helios/shared-domain` and
 * `@helios/shared-domain/stores`; `api/axiosInstance` is a deeper subpath and is not a
 * share key, so there is no guarantee that one axios instance spans every MFE. A
 * recovery *registered onto* an instance therefore covers only the copy the registering
 * module happened to hold — the projects MFE's chat sends, say, but not the shell's
 * Profile preference `PUT`, which can produce an identical rejection.
 *
 * `window` is one object per tab, shared by every MFE and every module copy. A single
 * listener consequently hears rejections from all of them, which is what makes the
 * recovery whole without the transport depending on any domain.
 */

/**
 * Dispatched on `window` for every rejection the shared axios instance is about to
 * surface. Not dispatched for cancellations, nor for a 401 that is transparently
 * replayed — those never reach a caller as failures.
 */
export const HTTP_REJECTION_EVENT = 'helios:http-rejection';

export interface HttpRejectionDetail {
  /** The rejection as the caller will receive it. Listeners MAY mutate it. */
  error: unknown;
}

/**
 * Announce a rejection. Dispatch is **synchronous**, so a listener may rewrite
 * `error.message` into something user-facing and be certain the caller sees it.
 *
 * Never throws: a listener that throws must not change what the caller receives, and
 * an environment without `window` (node, tests without jsdom) is simply a no-op.
 */
export function notifyHttpRejection(error: unknown): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }
  try {
    window.dispatchEvent(
      new CustomEvent<HttpRejectionDetail>(HTTP_REJECTION_EVENT, { detail: { error } })
    );
  } catch {
    // `CustomEvent` unavailable, or the environment refused the dispatch. Subscriber
    // failures are contained in `onHttpRejection`, so reaching here means the announcement
    // itself could not be made — the caller's rejection is what matters and it is already
    // in hand.
  }
}

/**
 * Subscribe to rejections. Returns an unsubscribe function.
 *
 * Handlers run synchronously inside the rejecting call's promise chain, so they should
 * stay cheap: start async work, do not await it.
 */
export function onHttpRejection(handler: (error: unknown) => void): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => undefined;
  }
  const listener = (event: Event): void => {
    try {
      handler((event as CustomEvent<HttpRejectionDetail>).detail?.error);
    } catch (handlerError) {
      // Contain the subscriber here rather than letting it escape into `dispatchEvent`.
      // A throwing listener would otherwise be reported as an uncaught page error while
      // the announcer is mid-rejection — noise attributed to whichever request happened
      // to fail. Logged, not swallowed silently, so the bug is still findable.
      console.error('HTTP rejection listener threw:', handlerError);
    }
  };
  window.addEventListener(HTTP_REJECTION_EVENT, listener);
  return () => window.removeEventListener(HTTP_REJECTION_EVENT, listener);
}
